import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CAMPOS_ACTIVO,
  ETIQUETA_CRITICIDAD,
  type CampoActivo,
  ZONA_HORARIA,
  formatearFecha,
  formatearFechaHora,
  type Criticidad,
  type EstadoActivo,
  type EstadoOT,
  type MedicionActivo,
  type Pagina,
} from '@cmms/shared';
import { api, consulta } from '../servicios/api.js';
import { GraficoMediciones } from '../componentes/grafico-mediciones.js';
import { SelectorFecha, SelectorLista, SelectorMes } from '../componentes/selectores.js';
import { useSesion } from '../servicios/sesion.js';
import { useEsMovil } from '../servicios/responsive.js';
import {
  Aviso,
  Cargando,
  CasillaVerificacion,
  CodigoQr,
  DialogoQrPublico,
  Dialogo,
  Indicador,
  EstadoActivoEtiqueta,
  EstadoOtEtiqueta,
  Etiqueta,
  GaleriaImagenes,
  Paginacion,
  Vacio,
  mensajeError,
  type AdjuntoImagen,
} from '../componentes/piezas.js';
import { IconoEditar } from '../componentes/iconos.js';
import { descargarQrActivo, etiquetaQrPng } from '../componentes/etiquetas-qr.js';
import { VisorQr } from '../componentes/visor-qr.js';
import { HistorialInventarioActivo, useInventariosDelActivo } from './inventarios.js';
import { pedirVincularRfid } from '../componentes/lector-rfid.js';
import { BotonVolver } from '../componentes/boton-volver.js';
import type { ReactNode } from 'react';
import { EnlaceRuta } from '../componentes/enlace-ruta.js';

function Dato({ titulo, valor }: { titulo: string; valor: ReactNode }): JSX.Element {
  return (
    <div>
      <div className="indicador-titulo">{titulo.toUpperCase()}</div>
      <div style={{ fontSize: 13.5, fontWeight: 600 }}>{valor}</div>
    </div>
  );
}

/**
 * Vistas de solo lectura de activo y ubicación (§16.6).
 *
 * Antes la única forma de ver la ficha de un activo o una ubicación era entrar
 * al formulario de edición — lo que además exigía permiso de escritura para
 * algo que era apenas consultar. Son rutas propias (no un modal) justamente
 * para que «Ubicación» en la ficha de un activo pueda enlazar a la ubicación,
 * y viceversa, sin importar desde qué módulo se llegó.
 */

interface ActivoDetalle {
  atributos: Record<string, string>;
  id: string;
  numero: number;
  nombre: string;
  tiposActivo: { id: string; nombre: string }[];
  ubicacionId: string | null;
  ubicacionNombre: string | null;
  ubicacionRaiz: string | null;
  activoPadreId: string | null;
  activoPadreNombre: string | null;
  criticidad: Criticidad | null;
  estado: EstadoActivo;
  motivoInactividad: string | null;
  descripcion: string | null;
  fabricante: string | null;
  modelo: string | null;
  numeroSerie: string | null;
  codigoQr: string | null;
  rfidUid: string | null;
  expiracionGarantia: string | null;
}

interface ObservacionActivo {
  id: string;
  ordenTrabajoNumero: number | null;
  texto: string;
  adjuntos: AdjuntoImagen[];
  fechaVencimiento: string | null;
  vencida: boolean;
  resuelta: boolean;
  ordenTrabajoGeneradaId: string | null;
  ordenTrabajoGeneradaNumero: number | null;
  ordenTrabajoGeneradaEstado: EstadoOT | null;
  creadoPor: string | null;
  creadoEl: string;
}

interface OrdenDelActivo {
  id: string;
  numero: number;
  titulo: string;
  estado: EstadoOT;
  fechaVencimiento: string | null;
  vencida: boolean;
}

export function DetalleActivo(): JSX.Element {
  const { id = '' } = useParams();
  const { puede } = useSesion();
  const cliente = useQueryClient();
  const [errorQr, setErrorQr] = useState<string | null>(null);
  const [visorQr, setVisorQr] = useState<{ imagen: string; titulo: string } | null>(null);
  const esMovil = useEsMovil();
  const { data: inventarios } = useInventariosDelActivo(id);
  const inventarioActual = inventarios?.[0];
  const { data: asignacion } = useQuery({
    queryKey: ['activo-asignaciones', id],
    queryFn: () =>
      api.get<{ vigente: { personaNombre: string } | null }>(`/activos/${id}/asignaciones`),
  });
  // Cada registro es una verificación del activo en una revisión: se cuentan todas, no solo los inventarios.
  const cantidadVerificaciones = inventarios?.length ?? 0;

  const { data: activo, isLoading } = useQuery({
    queryKey: ['activo', id],
    queryFn: () => api.get<ActivoDetalle>(`/activos/${id}`),
  });

  const { data: fotos } = useQuery({
    queryKey: ['adjuntos', 'ACTIVO', id],
    queryFn: () => api.get<AdjuntoImagen[]>(`/adjuntos${consulta({ entidad: 'ACTIVO', entidadId: id })}`),
  });

  const { data: observaciones } = useQuery({
    queryKey: ['observaciones-activo', id],
    queryFn: () => api.get<ObservacionActivo[]>(`/activos/${id}/observaciones`),
  });

  const { data: ordenes } = useQuery({
    queryKey: ['ordenes-trabajo', 'activo', id],
    queryFn: () =>
      api.get<Pagina<OrdenDelActivo>>(
        `/ordenes-trabajo${consulta({ activoId: id, tamano: 50, orden: 'numero', direccion: 'desc' })}`,
      ),
    enabled: puede('ot:leer'),
  });

  if (isLoading) return <Cargando />;
  if (!activo) return <Vacio titulo="El activo no existe" />;

  return (
    <>
      <BotonVolver a="/activos" etiqueta="Volver a Activos" />

      <div className="cabecera-pagina">
        <div>
          <div className="numero-registro">ACTIVO #{activo.numero}</div>
          <h1>{activo.nombre}</h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {puede('activo:escribir') ? (
            <button
              type="button"
              className="boton pequeno"
              onClick={() => pedirVincularRfid({ activoId: activo.id, nombre: activo.nombre })}
            >
              {activo.rfidUid ? 'Reemplazar tag RFID' : 'Vincular tag RFID'}
            </button>
          ) : null}
          {puede('activo:escribir') ? (
            <EnlaceRuta to={`/activos?editar=${activo.id}`} className="boton secundario pequeno">
              Editar
            </EnlaceRuta>
          ) : null}
          <EnlaceRuta to={`/activos`} className="boton secundario pequeno">
            Ir al listado
          </EnlaceRuta>
        </div>
      </div>

      <div className="panel">
        <div className="panel-cabecera">
          <h2>Ficha</h2>
          <EstadoActivoEtiqueta estado={activo.estado} />
        </div>
        <div className={`panel-cuerpo${activo.codigoQr ? ' ficha-con-qr' : ''}`}>
          <div>
            {fotos && fotos.length > 0 ? (
              <div style={{ marginBottom: 16 }}>
                <GaleriaImagenes fotos={fotos} titulo={(f) => `${activo.nombre} · ${f.nombreArchivo}`} />
              </div>
            ) : null}

            <div className="rejilla-campos rejilla-datos">
              <Dato
                titulo="Ubicación"
                valor={
                  activo.ubicacionId ? (
                    <EnlaceRuta to={`/ubicaciones/${activo.ubicacionId}`}>{activo.ubicacionNombre}</EnlaceRuta>
                  ) : (
                    '—'
                  )
                }
              />
              <Dato titulo="Sede" valor={activo.ubicacionRaiz ?? '—'} />
              <Dato titulo="Asignado a" valor={asignacion?.vigente?.personaNombre ?? 'Sin asignar'} />
              <Dato
                titulo="Pertenece a"
                valor={
                  activo.activoPadreId ? (
                    <EnlaceRuta to={`/activos/${activo.activoPadreId}`}>{activo.activoPadreNombre}</EnlaceRuta>
                  ) : (
                    '—'
                  )
                }
              />
              <Dato
                titulo="Inventario"
                valor={
                  inventarioActual ? (
                    <>
                      <EnlaceRuta to={`/activos/inventarios/${inventarioActual.inventarioId}`}>{inventarioActual.inventarioNombre}</EnlaceRuta>
                      <div style={{ fontSize: 11.5, fontWeight: 400, color: 'var(--texto-tenue)' }}>
                        {inventarioActual.revisionNombre} · {formatearFecha(inventarioActual.fecha)} · {inventarioActual.responsableNombre}
                      </div>
                    </>
                  ) : (
                    'Sin inventariar'
                  )
                }
              />
              <Dato
                titulo="Verificaciones"
                valor={`${cantidadVerificaciones} ${cantidadVerificaciones === 1 ? 'vez' : 'veces'} en inventario`}
              />
              <Dato
                titulo="Criticidad"
                valor={activo.criticidad ? ETIQUETA_CRITICIDAD[activo.criticidad] : 'Sin definir'}
              />
              <Dato
                titulo="Tipos"
                valor={activo.tiposActivo.length > 0 ? activo.tiposActivo.map((t) => t.nombre).join(', ') : '—'}
              />
              <Dato titulo="Fabricante" valor={activo.fabricante ?? '—'} />
              <Dato titulo="Modelo" valor={activo.modelo ?? '—'} />
              <Dato titulo="N.º de serie" valor={activo.numeroSerie ?? '—'} />
              {!activo.codigoQr ? <Dato titulo="Código QR" valor="—" /> : null}
              <Dato
                titulo="Tag RFID"
                valor={
                  activo.rfidUid ?? 'Sin vincular'
                }
              />
              {activo.expiracionGarantia ? (
                <Dato titulo="Garantía hasta" valor={formatearFechaHora(activo.expiracionGarantia)} />
              ) : null}
              {Object.entries(activo.atributos ?? {}).map(([campo, valor]) => {
                const definicion = CAMPOS_ACTIVO[campo as CampoActivo];
                // Un atributo sin valor se muestra vacío, no como «No».
                const texto =
                  valor === null || valor === ''
                    ? '—'
                    : definicion?.tipo === 'si_no'
                      ? valor === 'si'
                        ? 'Sí'
                        : 'No'
                      : definicion?.tipo === 'fecha'
                        ? formatearFecha(valor)
                        : valor;
                return <Dato key={campo} titulo={definicion?.etiqueta ?? campo} valor={texto} />;
              })}
            </div>

            {activo.motivoInactividad ? (
              <Aviso tono="atencion">Fuera de línea: {activo.motivoInactividad}</Aviso>
            ) : null}

            {activo.descripcion ? (
              <div style={{ marginTop: 14 }}>
                <div className="indicador-titulo">DESCRIPCIÓN</div>
                <p style={{ margin: '4px 0 0', fontSize: 13.5 }}>{activo.descripcion}</p>
              </div>
            ) : null}
          </div>

          {activo.codigoQr ? (
            <div className="ficha-qr-aside">
              <div className="indicador-titulo">CÓDIGO QR</div>
              <CodigoQr codigo={activo.codigoQr} />
              <button
                type="button"
                className="boton secundario pequeno"
                style={{ marginTop: 12 }}
                onClick={() => {
                  const qr = {
                    codigoQr: activo.codigoQr!,
                    numero: activo.numero,
                    nombre: activo.nombre,
                    ubicacionNombre: activo.ubicacionNombre,
                  };
                  // En el celular no se pueden bajar archivos: se muestra la imagen para guardarla.
                  const accion = esMovil
                    ? etiquetaQrPng(qr).then((imagen) => setVisorQr({ imagen, titulo: `#${activo.numero} ${activo.nombre}` }))
                    : descargarQrActivo(qr);
                  void accion.catch((error: unknown) => setErrorQr(mensajeError(error, 'No se pudo descargar el QR.')));
                }}
              >
                Descargar QR
              </button>
              {errorQr ? <Aviso tono="error">{errorQr}</Aviso> : null}
              {visorQr ? <VisorQr imagen={visorQr.imagen} titulo={visorQr.titulo} alCerrar={() => setVisorQr(null)} /> : null}
            </div>
          ) : null}
        </div>
      </div>

      {/* Observaciones y mediciones van lado a lado; en pantallas angostas se apilan solas. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16, marginTop: 16, alignItems: 'start' }}>
      <HistorialInventarioActivo activoId={id} />
      {observaciones && observaciones.length > 0 ? (
        <div className="panel">
          <div className="panel-cabecera">
            <h2>Observaciones</h2>
          </div>
          <div className="panel-cuerpo">
            <div className="hilo">
              {observaciones.map((obs) => (
                <article key={obs.id} className="hilo-evento" style={{ opacity: obs.resuelta ? 0.55 : 1 }}>
                  {/* Antes se usaba el mismo "✓" tanto para "ya tiene OT" como
                      para "resuelta" (campo `resuelta` existía pero nunca se
                      leía acá): una observación con OT generada pero aún
                      abierta se veía idéntica a una ya cerrada (§ bug
                      reportado). */}
                  <div className="hilo-marca" aria-hidden="true" style={obs.vencida ? { background: 'var(--falla)' } : undefined}>
                    {obs.resuelta ? '✓' : obs.ordenTrabajoGeneradaId ? '…' : '!'}
                  </div>
                  <div className="hilo-cuerpo">
                    <div className="hilo-meta" style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span>
                        Hecho por {obs.creadoPor ?? 'Sistema'} · {formatearFechaHora(obs.creadoEl)}
                        {obs.ordenTrabajoNumero ? ` · OT #${obs.ordenTrabajoNumero}` : ''}
                        {!obs.ordenTrabajoGeneradaId && obs.fechaVencimiento ? (
                          <span style={obs.vencida ? { color: 'var(--falla)', fontWeight: 700 } : undefined}>
                            {' · '}
                            {obs.vencida ? 'venció el' : 'atender antes del'} {formatearFecha(obs.fechaVencimiento)}
                          </span>
                        ) : null}
                      </span>
                      {obs.ordenTrabajoGeneradaId ? (
                        <EnlaceRuta to={`/ordenes/${obs.ordenTrabajoGeneradaId}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          OT #{obs.ordenTrabajoGeneradaNumero}
                          {obs.ordenTrabajoGeneradaEstado ? <EstadoOtEtiqueta estado={obs.ordenTrabajoGeneradaEstado} /> : null}
                        </EnlaceRuta>
                      ) : puede('ot:crear') ? (
                        <Link
                          to={`/ordenes/nueva?activoId=${activo.id}&observacionId=${obs.id}&titulo=${encodeURIComponent(
                            `Seguimiento: ${obs.texto.slice(0, 80)}`,
                          )}`}
                          className="boton secundario pequeno"
                        >
                          Crear OT
                        </Link>
                      ) : null}
                    </div>
                    <div className="hilo-texto">{obs.texto}</div>
                    <GaleriaImagenes fotos={obs.adjuntos} titulo={(f) => `Observación · ${f.nombreArchivo}`} />
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {puede('medidor:leer') ? <SeccionMediciones activoId={activo.id} /> : null}
      </div>

      <SeccionHistorialCambios activoId={activo.id} />

      {ordenes && ordenes.datos.length > 0 ? (
        <div className="panel" style={{ marginTop: 16 }}>
          <div className="panel-cabecera">
            <h2>Historial de órdenes de trabajo</h2>
          </div>
          <div className="tabla-contenedor">
            <table className="tabla">
              <thead>
                <tr>
                  <th>N.º</th>
                  <th>Título</th>
                  <th>Estado</th>
                  <th>Vencimiento</th>
                </tr>
              </thead>
              <tbody>
                {ordenes.datos.map((o: OrdenDelActivo) => (
                  <tr key={o.id}>
                    <td className="numero-registro">#{o.numero}</td>
                    <td>
                      <EnlaceRuta to={`/ordenes/${o.id}`}>{o.titulo}</EnlaceRuta>
                    </td>
                    <td>
                      <EstadoOtEtiqueta estado={o.estado} vencida={o.vencida} />
                    </td>
                    <td>{o.fechaVencimiento ? formatearFechaHora(o.fechaVencimiento) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </>
  );
}

/** Con más medidores que esto, el activo muestra solo estas gráficas y deja elegir cuáles ver. */
const GRAFICAS_INICIALES = 3;

/**
 * Mediciones de los medidores del activo: una gráfica de tendencia por medidor,
 * fija en la pestaña. Cada medidor tiene su unidad, así que cada gráfica tiene
 * sus propios ejes. Si hay más de tres medidores, se ven los tres primeros y un
 * botón permite elegir cuáles mostrar. Cada gráfica tiene su «Ver detalle».
 */
function SeccionMediciones({ activoId }: { activoId: string }): JSX.Element {
  const [elegidosIds, setElegidosIds] = useState<string[] | null>(null);
  const [eligiendo, setEligiendo] = useState(false);
  const [detalleDe, setDetalleDe] = useState<string | null>(null);
  const esMovil = useEsMovil();

  const { data: mediciones, isLoading, error } = useQuery({
    queryKey: ['mediciones-activo', activoId],
    queryFn: () => api.get<MedicionActivo[]>(`/medidores/activo/${activoId}/mediciones`),
  });

  const medidores = [...new Map((mediciones ?? []).map((m) => [m.medidorId, m])).values()];
  const visiblesIds = elegidosIds ?? medidores.slice(0, GRAFICAS_INICIALES).map((m) => m.medidorId);
  const visibles = medidores.filter((m) => visiblesIds.includes(m.medidorId));

  const alternar = (medidorId: string): void => {
    setElegidosIds(
      visiblesIds.includes(medidorId) ? visiblesIds.filter((id) => id !== medidorId) : [...visiblesIds, medidorId],
    );
  };

  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Mediciones</h2>
        {medidores.length > GRAFICAS_INICIALES ? (
          <button
            type="button"
            className="boton secundario pequeno"
            aria-expanded={eligiendo}
            onClick={() => setEligiendo((abierto) => !abierto)}
          >
            Elegir gráficas ({visibles.length} de {medidores.length})
          </button>
        ) : null}
      </div>
      <div className="panel-cuerpo" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {isLoading ? (
          <Cargando />
        ) : error ? (
          <Aviso tono="atencion">{mensajeError(error, 'No se pudieron cargar las mediciones.')}</Aviso>
        ) : !mediciones || mediciones.length === 0 ? (
          <Vacio titulo="Este activo aún no tiene mediciones" detalle="Las lecturas de sus medidores aparecerán aquí." />
        ) : (
          <>
            {eligiendo ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 18px' }}>
                {medidores.map((m) => (
                  <CasillaVerificacion
                    key={m.medidorId}
                    marcada={visiblesIds.includes(m.medidorId)}
                    alCambiar={() => alternar(m.medidorId)}
                    etiqueta={m.medidorNombre}
                  />
                ))}
              </div>
            ) : null}

            {visibles.length === 0 ? <Aviso>Marca al menos una gráfica para verla.</Aviso> : null}

            <div style={{ display: 'grid', gridTemplateColumns: esMovil ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: 16 }}>
            {visibles.map((medidor) => {
              const serie = mediciones.filter((m) => m.medidorId === medidor.medidorId);
              return (
                <div key={medidor.medidorId}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                    <strong>
                      {medidor.medidorNombre}
                      {medidor.unidadMedida ? (
                        <span style={{ fontWeight: 400, color: 'var(--texto-tenue)' }}> · {medidor.unidadMedida}</span>
                      ) : null}
                    </strong>
                    <button type="button" className="boton secundario pequeno" onClick={() => setDetalleDe(medidor.medidorId)}>
                      Ver detalle
                    </button>
                  </div>
                  {serie.length < 2 ? (
                    <Vacio
                      titulo="Aún no hay suficientes mediciones para graficar"
                      detalle="Registra al menos dos mediciones de este medidor para ver la tendencia."
                    />
                  ) : (
                    <GraficoMediciones puntos={serie} unidad={medidor.unidadMedida} />
                  )}
                </div>
              );
            })}
            </div>
          </>
        )}
      </div>

      {detalleDe && mediciones ? (
        <ModalDetalleMediciones mediciones={mediciones} medidorInicialId={detalleDe} alCerrar={() => setDetalleDe(null)} />
      ) : null}
    </div>
  );
}

const ETIQUETA_ORIGEN: Record<MedicionActivo['origen'], string> = {
  MANUAL: 'Manual',
  PROCEDIMIENTO: 'Procedimiento',
  AUTOMATIZADO: 'Automatizado',
};

const COLOR_ORIGEN: Record<MedicionActivo['origen'], string> = {
  MANUAL: 'var(--azul)',
  PROCEDIMIENTO: 'var(--alerta)',
  AUTOMATIZADO: 'var(--exito)',
};

/**
 * Detalle de las mediciones del activo. El título es el selector de medidor.
 * Debajo, los KPIs (mismo formato que el dashboard) y dos columnas de la
 * misma altura: a la izquierda los filtros de fecha y la gráfica; a la
 * derecha el detalle, que empieza al nivel de los filtros y termina al nivel
 * de la base de la gráfica. Solo el detalle tiene scroll. Pulsar un punto de
 * la gráfica resalta su detalle, y pulsar un detalle resalta su punto.
 * Siempre dibuja un solo medidor, porque sus valores pueden venir en unidades
 * distintas.
 */
function ModalDetalleMediciones({
  mediciones,
  medidorInicialId,
  alCerrar,
}: {
  mediciones: MedicionActivo[];
  medidorInicialId: string | null;
  alCerrar: () => void;
}): JSX.Element {
  const medidores = [...new Map(mediciones.map((m) => [m.medidorId, m])).values()];
  const [medidorId, setMedidorId] = useState(medidorInicialId ?? medidores[0]?.medidorId ?? '');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [seleccionadoId, setSeleccionadoId] = useState<string | null>(null);
  const columnaRef = useRef<HTMLDivElement>(null);

  const medidorActual = medidores.find((m) => m.medidorId === medidorId) ?? medidores[0];

  // AAAA-MM-DD de cada lectura en la zona horaria del proyecto, para comparar
  // con los filtros de fecha (que son fechas, no instantes).
  const fmtDia = new Intl.DateTimeFormat('en-CA', { timeZone: ZONA_HORARIA, year: 'numeric', month: '2-digit', day: '2-digit' });

  const filtradas = mediciones
    .filter((m) => m.medidorId === medidorId)
    .filter((m) => {
      const dia = fmtDia.format(new Date(m.fechaHora));
      return (!desde || dia >= desde) && (!hasta || dia <= hasta);
    })
    .sort((a, b) => new Date(a.fechaHora).getTime() - new Date(b.fechaHora).getTime());

  const valores = filtradas.map((m) => m.valor);
  const resumen = valores.length
    ? {
        ultimo: valores[valores.length - 1]!,
        minimo: Math.min(...valores),
        maximo: Math.max(...valores),
        promedio: valores.reduce((s, v) => s + v, 0) / valores.length,
        cambio: valores.length >= 2 ? valores[valores.length - 1]! - valores[0]! : null,
      }
    : null;

  const unidad = medidorActual?.unidadMedida ?? null;
  const formatoNumero = (n: number): string => (Number.isInteger(n) ? String(n) : n.toFixed(1));
  const hayFiltros = Boolean(desde || hasta);

  // Rango de búsqueda: desde la medición más antigua del activo hasta hoy.
  // No hay mediciones futuras, así que el límite superior es el día de hoy.
  const primeraMedicion = Math.min(...mediciones.map((m) => new Date(m.fechaHora).getTime()));
  const primeraClave = Number.isFinite(primeraMedicion) ? fmtDia.format(primeraMedicion) : undefined;
  const hoyClave = fmtDia.format(new Date());

  // Al elegir una lectura desde la gráfica, su tarjeta se lleva a la vista
  // dentro de la columna de detalle (que tiene su propio scroll).
  useEffect(() => {
    if (!seleccionadoId) return;
    columnaRef.current
      ?.querySelector<HTMLElement>(`[data-medicion="${seleccionadoId}"]`)
      ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [seleccionadoId]);

  return (
    <Dialogo
      altoFijo
      titulo={
        <div style={{ minWidth: 240, maxWidth: 380 }}>
          <SelectorLista
            id="detalle-medidor"
            buscador
            valor={medidorId}
            alCambiar={(v) => {
              setMedidorId(v);
              setSeleccionadoId(null);
            }}
            opciones={medidores.map((m) => ({ id: m.medidorId, nombre: m.medidorNombre }))}
            requerido
          />
        </div>
      }
      ancho={1100}
      alCerrar={alCerrar}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, flex: 1, minHeight: 0 }}>
        {resumen ? (
          <div className="rejilla-indicadores" style={{ marginBottom: 0 }}>
            <Indicador
              titulo="ÚLTIMA"
              valor={`${formatoNumero(resumen.ultimo)}${unidad ? ` ${unidad}` : ''}`}
              className="indicador-azul"
            />
            <Indicador titulo="MÍNIMO" valor={formatoNumero(resumen.minimo)} className="indicador-verde" />
            <Indicador titulo="MÁXIMO" valor={formatoNumero(resumen.maximo)} className="indicador-roja" />
            <Indicador titulo="PROMEDIO" valor={formatoNumero(resumen.promedio)} className="indicador-gris" />
            <Indicador
              titulo="CAMBIO"
              valor={
                resumen.cambio === null
                  ? '—'
                  : resumen.cambio > 0
                    ? `↑ +${formatoNumero(resumen.cambio)}`
                    : resumen.cambio < 0
                      ? `↓ ${formatoNumero(resumen.cambio)}`
                      : 'Sin cambio'
              }
              className="indicador-ambar"
            />
          </div>
        ) : null}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 2fr) minmax(240px, 1fr)',
            gap: 16,
            flex: 1,
            minHeight: 0,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0, minHeight: 0 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 0', minWidth: 0 }}>
                <SelectorFecha
                  id="detalle-desde"
                  etiqueta="Desde"
                  valor={desde}
                  alCambiar={setDesde}
                  minimo={primeraClave}
                  maximo={hoyClave}
                />
              </div>
              <div style={{ flex: '1 1 0', minWidth: 0 }}>
                <SelectorFecha
                  id="detalle-hasta"
                  etiqueta="Hasta"
                  valor={hasta}
                  alCambiar={setHasta}
                  minimo={primeraClave}
                  maximo={hoyClave}
                />
              </div>
              {/* Solo ocupa sitio cuando hay filtros; el margen inferior iguala al
                  de los campos de fecha para quedar alineado con sus disparadores. */}
              {hayFiltros ? (
                <div style={{ flex: '0 0 auto', marginBottom: 14 }}>
                  <button
                    type="button"
                    className="boton secundario"
                    style={{ height: 38, padding: '0 14px' }}
                    onClick={() => {
                      setDesde('');
                      setHasta('');
                    }}
                  >
                    Limpiar
                  </button>
                </div>
              ) : null}
            </div>

            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              {filtradas.length < 2 ? (
                <Vacio
                  titulo="No hay suficientes mediciones con estos filtros"
                  detalle="Ajusta las fechas para ver la tendencia."
                />
              ) : (
                <GraficoMediciones
                  puntos={filtradas}
                  unidad={unidad}
                  alto="100%"
                  seleccionadoId={seleccionadoId}
                  alSeleccionar={setSeleccionadoId}
                />
              )}
            </div>
          </div>

          <div
            ref={columnaRef}
            style={{
              minHeight: 0,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              paddingRight: 4,
            }}
          >
            {/* Pegado arriba de la columna: el título no se pierde al hacer scroll. */}
            <div
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                background: 'var(--panel)',
                fontSize: 12,
                fontWeight: 700,
                color: 'var(--texto-medio)',
                paddingBottom: 4,
              }}
            >
              DETALLE ({filtradas.length})
            </div>
            {filtradas.length === 0 ? (
              <Vacio titulo="Sin mediciones con estos filtros" />
            ) : (
              [...filtradas].reverse().map((m) => {
                const activa = m.id === seleccionadoId;
                return (
                  <button
                    type="button"
                    key={m.id}
                    data-medicion={m.id}
                    aria-pressed={activa}
                    onClick={() => setSeleccionadoId(m.id)}
                    style={{
                      textAlign: 'left',
                      color: 'inherit',
                      font: 'inherit',
                      cursor: 'pointer',
                      background: activa ? 'var(--azul-suave)' : 'var(--panel)',
                      border: `1px solid ${activa ? 'var(--azul)' : 'var(--borde)'}`,
                      borderLeft: `4px solid ${COLOR_ORIGEN[m.origen]}`,
                      borderRadius: 8,
                      padding: '8px 10px',
                      boxShadow: activa ? '0 0 0 2px var(--azul-tenue)' : undefined,
                      flex: 'none',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                      <span style={{ fontSize: 12.5, color: 'var(--texto-medio)' }}>{formatearFechaHora(m.fechaHora)}</span>
                      <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--azul)' }}>
                        {m.valor}
                        {unidad ? <span style={{ fontSize: 11.5, fontWeight: 400, color: 'var(--texto-tenue)' }}> {unidad}</span> : null}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', fontSize: 12, color: 'var(--texto-tenue)', marginTop: 4 }}>
                      <span style={{ color: COLOR_ORIGEN[m.origen], fontWeight: 600 }}>{ETIQUETA_ORIGEN[m.origen]}</span>
                      {m.registradoPor ? <span>por {m.registradoPor}</span> : null}
                      {m.editadoEl ? <span>editado {formatearFechaHora(m.editadoEl)}</span> : null}
                    </div>
                    {m.nota ? <div style={{ fontSize: 12.5, marginTop: 4 }}>{m.nota}</div> : null}
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>
    </Dialogo>
  );
}

interface UbicacionDetalle {
  id: string;
  nombre: string;
  ubicacionPadreId: string | null;
  nombrePadre: string | null;
  tipoUbicacionId: string | null;
  tipoUbicacionNombre: string | null;
  direccion: string | null;
  direccionEfectiva: string | null;
  descripcion: string | null;
  codigoQr: string | null;
  gruposACargo: { id: string; nombre: string }[];
  subUbicaciones: number;
  activos: number;
}

interface SubUbicacion {
  id: string;
  nombre: string;
}

interface ActivoEnUbicacion {
  id: string;
  numero: number;
  nombre: string;
  estado: EstadoActivo;
}

interface ItemChecklistUbicacion {
  id: string;
  texto: string;
  cumple: boolean;
  activo: boolean;
}

interface ObservacionUbicacion {
  id: string;
  ordenTrabajoNumero: number | null;
  texto: string;
  adjuntos: AdjuntoImagen[];
  fechaVencimiento: string | null;
  vencida: boolean;
  resuelta: boolean;
  ordenTrabajoGeneradaId: string | null;
  ordenTrabajoGeneradaNumero: number | null;
  ordenTrabajoGeneradaEstado: EstadoOT | null;
  creadoEl: string;
}

interface EventoHistorialActivo {
  id: string;
  tipoEvento: string;
  usuario: string | null;
  fechaHora: string;
  texto: string | null;
  descripcion: string;
  cambios: { campo: string; anterior: string; nuevo: string }[];
}

/** Todo lo que se cambió en la ficha del activo, del más reciente al más antiguo. */
function SeccionHistorialCambios({ activoId }: { activoId: string }): JSX.Element | null {
  const { data: eventos } = useQuery({
    queryKey: ['activo-historial-cambios', activoId],
    queryFn: () => api.get<EventoHistorialActivo[]>(`/activos/${activoId}/historial-cambios`),
  });

  if (!eventos || eventos.length === 0) return null;

  return (
    <div className="panel" style={{ marginTop: 16 }}>
      <div className="panel-cabecera">
        <h2>Historial de cambios</h2>
      </div>
      <div className="panel-cuerpo">
        <div className="hilo">
          {eventos.map((e) => (
            <article key={e.id} className="hilo-evento">
              <div className="hilo-marca" aria-hidden="true">•</div>
              <div className="hilo-cuerpo">
                <div className="hilo-meta">
                  {formatearFechaHora(e.fechaHora)} · Hecho por {e.usuario ?? 'sistema'}
                </div>
                <div className="hilo-texto">
                  {e.descripcion}
                  {e.texto ? ` — ${e.texto}` : ''}
                </div>
                {e.cambios.length > 0 ? (
                  <details style={{ marginTop: 4 }}>
                    <summary style={{ cursor: 'pointer', fontSize: 12.5, fontWeight: 700 }}>
                      Ver detalles ({e.cambios.length})
                    </summary>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6, fontSize: 13 }}>
                      {e.cambios.map((c, i) => (
                        <div key={i}>
                          <strong>{c.campo}:</strong> {c.anterior} → {c.nuevo}
                        </div>
                      ))}
                    </div>
                  </details>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Réplica del panel de observaciones del activo (§16.3), para OT que apuntan a la ubicación sin un activo puntual. */
function ObservacionesUbicacion({ ubicacionId }: { ubicacionId: string }): JSX.Element | null {
  const { puede } = useSesion();

  const { data: observaciones } = useQuery({
    queryKey: ['observaciones-ubicacion', ubicacionId],
    queryFn: () => api.get<ObservacionUbicacion[]>(`/ubicaciones/${ubicacionId}/observaciones`),
  });

  if (!observaciones || observaciones.length === 0) return null;

  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Observaciones</h2>
      </div>
      <div className="panel-cuerpo">
        <div className="hilo">
          {observaciones.map((obs) => (
            <article key={obs.id} className="hilo-evento" style={{ opacity: obs.resuelta ? 0.55 : 1 }}>
              <div className="hilo-marca" aria-hidden="true" style={obs.vencida ? { background: 'var(--falla)' } : undefined}>
                {obs.resuelta ? '✓' : obs.ordenTrabajoGeneradaId ? '…' : '!'}
              </div>
              <div className="hilo-cuerpo">
                <div className="hilo-meta" style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span>
                    {formatearFechaHora(obs.creadoEl)}
                    {obs.ordenTrabajoNumero ? ` · OT #${obs.ordenTrabajoNumero}` : ''}
                    {!obs.ordenTrabajoGeneradaId && obs.fechaVencimiento ? (
                      <span style={obs.vencida ? { color: 'var(--falla)', fontWeight: 700 } : undefined}>
                        {' · '}
                        {obs.vencida ? 'venció el' : 'atender antes del'} {formatearFecha(obs.fechaVencimiento)}
                      </span>
                    ) : null}
                  </span>
                  {obs.ordenTrabajoGeneradaId ? (
                    <EnlaceRuta to={`/ordenes/${obs.ordenTrabajoGeneradaId}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      OT #{obs.ordenTrabajoGeneradaNumero}
                      {obs.ordenTrabajoGeneradaEstado ? <EstadoOtEtiqueta estado={obs.ordenTrabajoGeneradaEstado} /> : null}
                    </EnlaceRuta>
                  ) : puede('ot:crear') ? (
                    <Link
                      to={`/ordenes/nueva?ubicacionId=${ubicacionId}&observacionId=${obs.id}&titulo=${encodeURIComponent(
                        `Seguimiento: ${obs.texto.slice(0, 80)}`,
                      )}`}
                      className="boton secundario pequeno"
                    >
                      Crear OT
                    </Link>
                  ) : null}
                </div>
                <div className="hilo-texto">{obs.texto}</div>
                <GaleriaImagenes fotos={obs.adjuntos} titulo={(f) => `Observación · ${f.nombreArchivo}`} />
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Checklist de verificadores de la ubicación (§11): a mano, por ubicación
 * puntual — «tiene perchero, silla, calefacción» — para que al entrar a la
 * ficha se vea de un vistazo qué falta, sin plantilla por tipo de ubicación.
 *
 * La ficha es de solo marcar: crear ítems, editar su texto y activar/
 * desactivarlos vive en el modal de gestión (ícono de lápiz), para no mezclar
 * "marcar lo que falta hoy" con "administrar qué se verifica aquí".
 */
function ChecklistUbicacion({ ubicacionId }: { ubicacionId: string }): JSX.Element {
  const { puede } = useSesion();
  const cliente = useQueryClient();
  const [gestionAbierta, setGestionAbierta] = useState(false);
  const escribe = puede('ubicacion:escribir');

  const { data: items } = useQuery({
    queryKey: ['checklist-ubicacion', ubicacionId],
    queryFn: () => api.get<ItemChecklistUbicacion[]>(`/ubicaciones/${ubicacionId}/checklist`),
  });

  const refrescar = (): Promise<void> =>
    cliente.invalidateQueries({ queryKey: ['checklist-ubicacion', ubicacionId] }).then(() => undefined);

  const alternar = useMutation({
    mutationFn: (item: ItemChecklistUbicacion) =>
      api.put(`/ubicaciones/${ubicacionId}/checklist/${item.id}`, {
        texto: item.texto,
        cumple: !item.cumple,
        activo: item.activo,
      }),
    onSuccess: refrescar,
  });

  const activos = (items ?? []).filter((i) => i.activo);
  const pendientes = activos.filter((i) => !i.cumple).length;

  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Checklist de verificadores</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {activos.length > 0 ? (
            <Etiqueta tono={pendientes > 0 ? 'ambar' : 'verde'}>
              {pendientes > 0 ? `${pendientes} pendiente${pendientes === 1 ? '' : 's'}` : 'Completo'}
            </Etiqueta>
          ) : null}
          {escribe ? (
            <button
              type="button"
              className="boton-icono"
              title="Gestionar checklist"
              aria-label="Gestionar checklist"
              onClick={() => setGestionAbierta(true)}
            >
              <IconoEditar />
            </button>
          ) : null}
        </div>
      </div>
      <div className="panel-cuerpo">
        {activos.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--texto-tenue)', fontSize: 13.5 }}>
            Sin ítems activos todavía.
            {escribe ? ' Agrégalos desde el ícono de gestión, arriba a la derecha.' : ''}
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {activos.map((item) => (
              <div key={item.id} className="fila-checklist">
                <CasillaVerificacion
                  marcada={item.cumple}
                  deshabilitada={!escribe || alternar.isPending}
                  etiqueta={item.texto}
                  alCambiar={() => alternar.mutate(item)}
                />
                <span style={item.cumple ? { textDecoration: 'line-through', color: 'var(--texto-tenue)' } : undefined}>
                  {item.texto}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {gestionAbierta ? (
        <ModalGestionChecklistUbicacion ubicacionId={ubicacionId} alCerrar={() => setGestionAbierta(false)} />
      ) : null}
    </div>
  );
}

/**
 * Gestión del checklist: alta de ítems, edición de texto y activar/desactivar.
 * Desactivar (no eliminar) es la vía normal para «esto ya no se verifica
 * acá» — el ítem sigue existiendo por si se reactiva más adelante, y su
 * historial de cumple/no-cumple no se pierde.
 */
function ModalGestionChecklistUbicacion({
  ubicacionId,
  alCerrar,
}: {
  ubicacionId: string;
  alCerrar: () => void;
}): JSX.Element {
  const cliente = useQueryClient();
  const [nuevoTexto, setNuevoTexto] = useState('');

  const { data: items } = useQuery({
    queryKey: ['checklist-ubicacion', ubicacionId],
    queryFn: () => api.get<ItemChecklistUbicacion[]>(`/ubicaciones/${ubicacionId}/checklist`),
  });

  const refrescar = (): Promise<void> =>
    cliente.invalidateQueries({ queryKey: ['checklist-ubicacion', ubicacionId] }).then(() => undefined);

  const crear = useMutation({
    mutationFn: (texto: string) =>
      api.post(`/ubicaciones/${ubicacionId}/checklist`, { texto, cumple: false, activo: true }),
    onSuccess: async () => {
      setNuevoTexto('');
      await refrescar();
    },
  });

  const guardar = useMutation({
    mutationFn: (item: ItemChecklistUbicacion) =>
      api.put(`/ubicaciones/${ubicacionId}/checklist/${item.id}`, {
        texto: item.texto,
        cumple: item.cumple,
        activo: item.activo,
      }),
    onSuccess: refrescar,
  });

  const eliminar = useMutation({
    mutationFn: (id: string) => api.delete(`/ubicaciones/${ubicacionId}/checklist/${id}`),
    onSuccess: refrescar,
  });

  return (
    <Dialogo titulo="Gestionar checklist de verificadores" ancho={580} alCerrar={alCerrar}>
      <p style={{ marginTop: 0, fontSize: 13, color: 'var(--texto-medio)' }}>
        Un ítem desactivado deja de verse en la ficha, pero queda registrado acá para reactivarlo después.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
        {(items ?? []).map((item) => (
          <FilaGestionChecklist
            key={item.id}
            item={item}
            guardando={guardar.isPending}
            alGuardar={(cambios) => guardar.mutate({ ...item, ...cambios })}
            alEliminar={() => eliminar.mutate(item.id)}
          />
        ))}
        {items && items.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--texto-tenue)', fontSize: 13.5 }}>Sin ítems todavía.</p>
        ) : null}
      </div>

      <form
        style={{ display: 'flex', gap: 8 }}
        onSubmit={(e) => {
          e.preventDefault();
          if (nuevoTexto.trim()) crear.mutate(nuevoTexto.trim());
        }}
      >
        <div className="campo" style={{ flex: 1, marginBottom: 0 }}>
          <input
            value={nuevoTexto}
            onChange={(e) => setNuevoTexto(e.target.value)}
            placeholder="Ej.: Extintor vigente"
            maxLength={300}
          />
        </div>
        <button type="submit" className="boton secundario" disabled={!nuevoTexto.trim() || crear.isPending}>
          Agregar
        </button>
      </form>

      {crear.isError ? <Aviso tono="error">{mensajeError(crear.error)}</Aviso> : null}
      {guardar.isError ? <Aviso tono="error">{mensajeError(guardar.error)}</Aviso> : null}
      {eliminar.isError ? <Aviso tono="error">{mensajeError(eliminar.error)}</Aviso> : null}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
        <button type="button" className="boton secundario" onClick={alCerrar}>
          Cerrar
        </button>
      </div>
    </Dialogo>
  );
}

function FilaGestionChecklist({
  item,
  guardando,
  alGuardar,
  alEliminar,
}: {
  item: ItemChecklistUbicacion;
  guardando: boolean;
  alGuardar: (cambios: Partial<Pick<ItemChecklistUbicacion, 'texto' | 'activo'>>) => void;
  alEliminar: () => void;
}): JSX.Element {
  const [texto, setTexto] = useState(item.texto);

  return (
    <div className={`fila-checklist-gestion${item.activo ? '' : ' inactivo'}`}>
      <CasillaVerificacion
        marcada={item.activo}
        etiqueta={item.activo ? `Desactivar ${item.texto}` : `Activar ${item.texto}`}
        deshabilitada={guardando}
        alCambiar={() => alGuardar({ activo: !item.activo })}
      />
      <div className="campo" style={{ flex: 1, marginBottom: 0 }}>
        <input
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onBlur={() => {
            const limpio = texto.trim();
            if (limpio && limpio !== item.texto) alGuardar({ texto: limpio });
            else if (!limpio) setTexto(item.texto);
          }}
          maxLength={300}
          disabled={guardando}
        />
      </div>
      <button type="button" className="fila-archivo-quitar" aria-label={`Eliminar ${item.texto}`} onClick={alEliminar}>
        ×
      </button>
    </div>
  );
}

const MAX_FILAS_LISTA = 10;

// La API solo acepta 25, 50, 75 o 100 por página.
const TAMANO_HISTORIAL = 25;

/** Primer y último día (YYYY-MM-DD) de un mes elegido como «YYYY-MM». */
function rangoDeMes(mes: string): { desde: string; hasta: string } | null {
  if (!mes) return null;
  const [anio, numeroMes] = mes.split('-').map(Number);
  const ultimoDia = new Date(anio!, numeroMes!, 0).getDate();
  return { desde: `${mes}-01`, hasta: `${mes}-${String(ultimoDia).padStart(2, '0')}` };
}

export function DetalleUbicacion(): JSX.Element {
  const [qrPublicoAbierto, setQrPublicoAbierto] = useState(false);
  const { id = '' } = useParams();
  const { puede } = useSesion();
  const [modalSubUbicacionesAbierto, setModalSubUbicacionesAbierto] = useState(false);
  const [modalActivosAbierto, setModalActivosAbierto] = useState(false);

  const { data: ubicacion, isLoading } = useQuery({
    queryKey: ['ubicacion', id],
    queryFn: () => api.get<UbicacionDetalle>(`/ubicaciones/${id}`),
  });

  const { data: hijas } = useQuery({
    queryKey: ['ubicaciones', 'padre', id],
    queryFn: () => api.get<{ datos: SubUbicacion[] }>(`/ubicaciones${consulta({ padreId: id, tamano: 100 })}`),
  });

  const { data: activos } = useQuery({
    queryKey: ['activos', 'ubicacion', id],
    queryFn: () => api.get<{ datos: ActivoEnUbicacion[]; total: number }>(`/activos${consulta({ ubicacionId: id, tamano: 100 })}`),
  });

  const { data: fotos } = useQuery({
    queryKey: ['adjuntos', 'UBICACION', id],
    queryFn: () => api.get<AdjuntoImagen[]>(`/adjuntos${consulta({ entidad: 'UBICACION', entidadId: id })}`),
  });

  // Dedup con la consulta que hace `ObservacionesUbicacion`: solo sirve para
  // decidir si hay algo que mostrar junto al checklist en 2 columnas.
  const { data: observaciones } = useQuery({
    queryKey: ['observaciones-ubicacion', id],
    queryFn: () => api.get<ObservacionUbicacion[]>(`/ubicaciones/${id}/observaciones`),
  });

  const esMovil = useEsMovil();
  const [historialAbierto, setHistorialAbierto] = useState(false);
  const [mesHistorial, setMesHistorial] = useState('');
  const [paginaHistorial, setPaginaHistorial] = useState(1);
  const rangoHistorial = rangoDeMes(mesHistorial);

  // Incluye las OT de la ubicación, de sus sub-ubicaciones y las de sus activos.
  // Se filtra por mes de creación y se pagina en el servidor.
  const { data: ordenes } = useQuery({
    queryKey: ['ordenes-trabajo', 'ubicacion', id, mesHistorial, paginaHistorial],
    queryFn: () =>
      api.get<Pagina<OrdenDelActivo>>(
        `/ordenes-trabajo${consulta({
          ubicacionId: id,
          creadoDesde: rangoHistorial?.desde,
          creadoHasta: rangoHistorial?.hasta,
          pagina: paginaHistorial,
          tamano: TAMANO_HISTORIAL,
          orden: 'numero',
          direccion: 'desc',
        })}`,
      ),
    enabled: puede('ot:leer'),
    placeholderData: keepPreviousData,
  });

  if (isLoading) return <Cargando />;
  if (!ubicacion) return <Vacio titulo="La ubicación no existe" />;

  return (
    <>
      <BotonVolver a="/ubicaciones" etiqueta="Volver a Ubicaciones" />

      <div className="cabecera-pagina">
        <div>
          {ubicacion.nombrePadre ? (
            <div className="numero-registro">
              <EnlaceRuta to={`/ubicaciones/${ubicacion.ubicacionPadreId}`}>{ubicacion.nombrePadre}</EnlaceRuta>
            </div>
          ) : null}
          <h1>{ubicacion.nombre}</h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {puede('ubicacion:escribir') ? (
            <EnlaceRuta to={`/ubicaciones?editar=${ubicacion.id}`} className="boton secundario pequeno">
              Editar
            </EnlaceRuta>
          ) : null}
          <EnlaceRuta to="/ubicaciones" className="boton secundario pequeno">
            Ir al listado
          </EnlaceRuta>
        </div>
      </div>

      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-cabecera">
          <h2>Ficha</h2>
        </div>
        <div className={`panel-cuerpo${ubicacion.codigoQr ? ' ficha-con-qr' : ''}`}>
          <div>
            {fotos && fotos.length > 0 ? (
              <div style={{ marginBottom: 16 }}>
                <GaleriaImagenes fotos={fotos} titulo={(f) => `${ubicacion.nombre} · ${f.nombreArchivo}`} />
              </div>
            ) : null}

            <div className="rejilla-campos rejilla-datos">
              {ubicacion.tipoUbicacionNombre ? <Dato titulo="Tipo" valor={ubicacion.tipoUbicacionNombre} /> : null}
              <Dato titulo="Dirección" valor={ubicacion.direccionEfectiva ?? ubicacion.direccion ?? '—'} />
              {!ubicacion.codigoQr ? <Dato titulo="Código QR" valor="—" /> : null}
              <Dato
                titulo="Grupos a cargo"
                valor={
                  ubicacion.gruposACargo.length > 0
                    ? ubicacion.gruposACargo.map((g) => g.nombre).join(', ')
                    : '—'
                }
              />
              <Dato titulo="Sub-ubicaciones" valor={ubicacion.subUbicaciones} />
              <Dato titulo="Activos localizados" valor={ubicacion.activos} />
            </div>

            {ubicacion.descripcion ? (
              <div style={{ marginTop: 14 }}>
                <div className="indicador-titulo">DESCRIPCIÓN</div>
                <p style={{ margin: '4px 0 0', fontSize: 13.5 }}>{ubicacion.descripcion}</p>
              </div>
            ) : null}
          </div>

          {ubicacion.codigoQr ? (
            <div className="ficha-qr-aside">
              <div className="indicador-titulo">CÓDIGO QR</div>
              <CodigoQr codigo={ubicacion.codigoQr} />
              {/* Solo sedes padres: genera el QR público para imprimir la hoja de la sede. */}
              {ubicacion.ubicacionPadreId === null ? (
                <button type="button" className="boton secundario pequeno" style={{ marginTop: 12 }} onClick={() => setQrPublicoAbierto(true)}>
                  Ver QR público
                </button>
              ) : null}
            </div>
          ) : null}
          {qrPublicoAbierto && ubicacion.codigoQr ? (
            <DialogoQrPublico
              codigo={ubicacion.codigoQr}
              nombre={ubicacion.nombre}
              alCerrar={() => setQrPublicoAbierto(false)}
            />
          ) : null}
        </div>
      </div>

      <div
        className={observaciones && observaciones.length > 0 ? 'detalle-ubicacion-rejilla' : undefined}
        style={{ marginBottom: 16 }}
      >
        <ChecklistUbicacion ubicacionId={id} />
        <ObservacionesUbicacion ubicacionId={id} />
      </div>

      {(hijas && hijas.datos.length > 0) || (activos && activos.datos.length > 0) ? (
        <div className={hijas?.datos.length && activos?.datos.length ? 'detalle-ubicacion-rejilla' : undefined}>
          {hijas && hijas.datos.length > 0 ? (
            <div className="panel">
              <div className="panel-cabecera">
                <h2>Sub-ubicaciones</h2>
                {hijas.datos.length > MAX_FILAS_LISTA ? (
                  <button type="button" className="boton-enlace" onClick={() => setModalSubUbicacionesAbierto(true)}>
                    Ver todas ({hijas.datos.length})
                  </button>
                ) : null}
              </div>
              <div className="panel-cuerpo" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {hijas.datos.slice(0, MAX_FILAS_LISTA).map((hija) => (
                  <EnlaceRuta key={hija.id} to={`/ubicaciones/${hija.id}`} className="fila-arbol" style={{ padding: '8px 6px' }}>
                    {hija.nombre}
                  </EnlaceRuta>
                ))}
              </div>
            </div>
          ) : null}

          {activos && activos.datos.length > 0 ? (
            <div className="panel">
              <div className="panel-cabecera">
                <h2>Activos en esta ubicación</h2>
                {activos.datos.length > MAX_FILAS_LISTA ? (
                  <button type="button" className="boton-enlace" onClick={() => setModalActivosAbierto(true)}>
                    Ver todos ({activos.datos.length})
                  </button>
                ) : null}
              </div>
              <div className="tabla-contenedor">
                <table className="tabla">
                  <thead>
                    <tr>
                      <th>N.º</th>
                      <th>Nombre</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activos.datos.slice(0, MAX_FILAS_LISTA).map((a) => (
                      <tr key={a.id}>
                        <td className="numero-registro">#{a.numero}</td>
                        <td>
                          <EnlaceRuta to={`/activos/${a.id}`}>{a.nombre}</EnlaceRuta>
                        </td>
                        <td>
                          <EstadoActivoEtiqueta estado={a.estado} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Con un mes elegido se muestra aunque no haya resultados, para poder cambiarlo. */}
      {ordenes && (ordenes.total > 0 || mesHistorial) ? (
        <div className="panel" style={{ marginTop: 16 }}>
          <div className="panel-cabecera">
            <h2>Historial de órdenes de trabajo ({ordenes.total})</h2>
            <button
              type="button"
              className="boton-enlace"
              aria-expanded={historialAbierto}
              onClick={() => setHistorialAbierto((abierto) => !abierto)}
            >
              {historialAbierto ? 'Ocultar' : 'Mostrar'}
            </button>
          </div>

          {historialAbierto ? (
            <>
              <div className="panel-cuerpo">
                <div style={{ maxWidth: 240 }}>
                  <SelectorMes
                    id="historial-mes"
                    etiqueta="Mes de creación"
                    valor={mesHistorial}
                    alCambiar={(clave) => {
                      setMesHistorial(clave);
                      setPaginaHistorial(1);
                    }}
                  />
                </div>
              </div>

              {ordenes.datos.length > 0 ? (
                <>
                  <div className="tabla-contenedor">
                    <table className="tabla">
                      <thead>
                        <tr>
                          <th>N.º</th>
                          <th>Título</th>
                          <th>Estado</th>
                          {esMovil ? null : <th>Vencimiento</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {ordenes.datos.map((o: OrdenDelActivo) => (
                          <tr key={o.id}>
                            <td className="numero-registro" style={{ whiteSpace: 'nowrap' }}>
                              #{o.numero}
                            </td>
                            <td style={{ minWidth: 140 }}>
                              <EnlaceRuta to={`/ordenes/${o.id}`}>{o.titulo}</EnlaceRuta>
                            </td>
                            <td>
                              <EstadoOtEtiqueta estado={o.estado} vencida={o.vencida} />
                            </td>
                            {esMovil ? null : (
                              <td>{o.fechaVencimiento ? formatearFechaHora(o.fechaVencimiento) : '—'}</td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Paginacion
                    pagina={ordenes.pagina}
                    tamano={ordenes.tamano}
                    total={ordenes.total}
                    onCambiar={setPaginaHistorial}
                  />
                </>
              ) : (
                <div className="panel-cuerpo">
                  <p style={{ margin: 0, fontSize: 13.5 }}>No hay órdenes de trabajo en ese mes.</p>
                </div>
              )}
            </>
          ) : null}
        </div>
      ) : null}

      {modalSubUbicacionesAbierto && hijas ? (
        <Dialogo titulo={`Sub-ubicaciones (${hijas.datos.length})`} alCerrar={() => setModalSubUbicacionesAbierto(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '60vh', overflowY: 'auto' }}>
            {hijas.datos.map((hija) => (
              <EnlaceRuta
                key={hija.id}
                to={`/ubicaciones/${hija.id}`}
                className="fila-arbol"
                style={{ padding: '8px 6px' }}
                onClick={() => setModalSubUbicacionesAbierto(false)}
              >
                {hija.nombre}
              </EnlaceRuta>
            ))}
          </div>
        </Dialogo>
      ) : null}

      {modalActivosAbierto && activos ? (
        <Dialogo titulo={`Activos en esta ubicación (${activos.datos.length})`} ancho={640} alCerrar={() => setModalActivosAbierto(false)}>
          <div className="tabla-contenedor" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            <table className="tabla">
              <thead>
                <tr>
                  <th>N.º</th>
                  <th>Nombre</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {activos.datos.map((a) => (
                  <tr key={a.id}>
                    <td className="numero-registro">#{a.numero}</td>
                    <td>
                      <EnlaceRuta to={`/activos/${a.id}`} onClick={() => setModalActivosAbierto(false)}>
                        {a.nombre}
                      </EnlaceRuta>
                    </td>
                    <td>
                      <EstadoActivoEtiqueta estado={a.estado} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Dialogo>
      ) : null}
    </>
  );
}
